import { CategoryType, UserType } from "@/types/user.types";
import { type ClassValue, clsx } from "clsx";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";
const CryptoJS = require("crypto-js");

import Instagram from "@/components/icons/instagram";
import Facebook from "@/components/icons/facebook";
import LinkedIn from "@/components/icons/linkedin";
import YouTube from "@/components/icons/youtube";
import X from "@/components/icons/x";
import { Url, URL } from "url";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getInitials(firstName: string, lastName: string = "") {
  return firstName.charAt(0).toUpperCase() + lastName?.charAt(0).toUpperCase();
}

export function textEllipses(text: string, sanitizedLength: number = 100) {
  return text
    ? text?.length > sanitizedLength
      ? `${text.slice(0, sanitizedLength).trim()}...`
      : text
    : "";
}

export function allowOnlyNumbers(event: any) {
  const allowedKeys = ["Backspace", "ArrowLeft", "ArrowRight", "Delete", "Tab"];

  const isNumber = /^[0-9]$/.test(event.key);
  const isAllowedKey = allowedKeys.includes(event.key);

  if (!isNumber && !isAllowedKey) {
    event.preventDefault();
  }
}

export function convertToHumanReadable(text: string, replacement: string) {
  return text.replace(/_/g, replacement);
}

export function formatToRupees(amount: string | number) {
  const formattedAmount = new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(+amount);

  return formattedAmount;
}

export function validateResetPasswordParamsToken(token: string) {
  let bytes = CryptoJS.AES.decrypt(
    token,
    process.env.NEXT_PUBLIC_RESET_PASSWORD_SECRET_KEY!,
  );
  let decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));

  const email = decryptedData.email;
  const expiration = decryptedData.expiration;
  const tokenGenerationTime = decryptedData.tokenGenerationTime;

  const isTokenValid = validateResetPasswordTokenExpiration({
    expiration,
    tokenGenerationTime,
  });

  if (isTokenValid) {
    toast.error(
      "Password resent link is invalid or expired. Please generate a new one",
      {
        position: "top-center",
      },
    );
    return {
      success: false,
      data: {},
    };
  }
  return {
    success: true,
    data: { email },
  };
}

export function validateResetPasswordTokenExpiration({
  expiration,
  tokenGenerationTime,
}: {
  expiration: string;
  tokenGenerationTime: string;
}) {
  // Extract the time unit and value from the expiration string (e.g., "5m")
  const timeValue = parseInt(expiration);

  const timeUnit = expiration?.slice(-1);

  // Convert the expiration time to milliseconds
  let expirationMs;
  switch (timeUnit) {
    case "s": // seconds
      expirationMs = timeValue * 1000;
      break;
    case "m": // minutes
      expirationMs = timeValue * 60 * 1000;
      break;
    case "h": // hours
      expirationMs = timeValue * 60 * 60 * 1000;
      break;
    default:
      expirationMs = 0;
    // TODO: show toaster and navigate to login
  }

  // Calculate the expiration time
  const expirationTime = new Date(tokenGenerationTime).getTime() + expirationMs;
  const currentTime = new Date().getTime();

  // Return true if the token has expired, otherwise false
  return currentTime > expirationTime;
}

export function randomRotation() {
  const rotation = Math.floor(Math.random() * 30) - 5;
  return `rotate(${rotation}deg)`;
}

export function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array]; // Create a copy to avoid mutating the original array
  for (let i = shuffled.length - 1; i > 0; i--) {
    const randomIndex = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[i]]; // Swap elements
  }
  return shuffled;
}
export function shareSocials(user: UserType) {
  const urlToShare = `${process.env.NEXT_PUBLIC_WEB_BASE_URL}/${user.user_name}`;
  navigator.clipboard
    .writeText(urlToShare)
    .then(() => {
      toast.success("Link copied to clipboard", {
        position: "top-center",
      });
    })
    .catch((err) => {});
}

export function formatHumanReadable(input: string): string {
  return (
    input
      // Insert space before uppercase letters (handles camelCase and PascalCase)
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      // Replace dashes, underscores, and dots with spaces (handles kebab-case, snake_case, dot.separated)
      .replace(/[-_.]/g, " ")
      // Convert to lowercase and trim extra spaces
      .toLowerCase()
      .replace(/\s+/g, " ") // Replace multiple spaces with a single space
      .trim()
      // Capitalize first letter of each word
      .replace(/\b\w/g, (char) => char.toUpperCase())
  );
}

export function toHumanReadable<T extends string>(
  text: T,
  acronyms: string[] = [],
): string {
  const ACRONYM_SET = new Set(acronyms.map((a) => a.toUpperCase()));

  return text
    .split("_")
    .map((word) => {
      const upperWord = word.toUpperCase();
      return ACRONYM_SET.has(upperWord)
        ? upperWord
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

export type FileValidationConfig = {
  requiredMessage?: string;
  allowedTypes?: string[];
  typeErrorMessage?: string;
  maxSizeInMB?: number;
  maxSizeMessage?: string;
};

export function validateFile(
  file: unknown,
  config: FileValidationConfig = {},
): string | null {
  const {
    requiredMessage = "Please upload a file.",
    allowedTypes = ["image/gif"],
    typeErrorMessage = "Invalid file format.",
    maxSizeInMB = 10,
    maxSizeMessage = `File size exceeds the ${maxSizeInMB} MB limit. Please upload a smaller file.`,
  } = config;

  if (!(file instanceof File)) {
    return requiredMessage;
  }

  if (file.size === 0) {
    return requiredMessage;
  }

  if (!allowedTypes.includes(file.type)) {
    return typeErrorMessage;
  }

  if (file.size > maxSizeInMB * 1024 * 1024) {
    return maxSizeMessage;
  }

  return null; // Valid file
}

export const withInert = (condition: boolean) =>
  condition ? { inert: "true" as unknown as never } : {};

export function areArraysEqual(a: any[], b: any[]): boolean {
  if (a.length !== b.length) return false;

  const countMap = new Map<any, number>();

  for (const item of a) {
    countMap.set(item, (countMap.get(item) || 0) + 1);
  }

  for (const item of b) {
    if (!countMap.has(item)) return false;
    countMap.set(item, countMap.get(item)! - 1);
    if (countMap.get(item) === 0) countMap.delete(item);
  }

  return countMap.size === 0;
}
export function findCategoryIds(
  categories?: string[],
  allCategories?: CategoryType[],
) {
  return allCategories
    ?.filter((cat) => categories?.includes(cat.category_name))
    ?.map((cat) => cat.id.toString());
}

export function deepEqual(obj1: any, obj2: any): boolean {
  if (obj1 === obj2) return true;

  if (
    typeof obj1 !== "object" ||
    obj1 === null ||
    typeof obj2 !== "object" ||
    obj2 === null
  ) {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    if (!keys2.includes(key)) return false;

    const val1 = obj1[key];
    const val2 = obj2[key];

    const areObjects = typeof val1 === "object" && typeof val2 === "object";

    if (
      (areObjects && !deepEqual(val1, val2)) ||
      (!areObjects && val1 !== val2)
    ) {
      return false;
    }
  }

  return true;
}

export function getUrl(url: string): Url {
  if (url.startsWith("https://")) return url as unknown as Url;
  else return ("https://" + url) as unknown as Url;
}

export const getEmail = (object: any) => {
  const { recipient, subject } = object;
  return `mailto:${recipient}?subject=${encodeURIComponent(
    subject,
  )}&body=${encodeURIComponent(`Dear Hallasheri Team,

Regards,

  `)}`;
};

export const formatAmount = (amount: number) => {
  return new Intl.NumberFormat("en-US", {
    // style: "currency",
    // currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};
export const formatDate = (timestamp: string) => {
  return new Date(timestamp).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const DOTS = "...";

export function getPaginationRange(
  current: number,
  total: number,
): (number | string)[] {
  const pages: (number | string)[] = [];

  if (total <= 5) {
    // Small page count, show all
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
    return pages;
  }

  // Always show first page
  pages.push(1);

  // Show left DOTS if needed
  if (current > 3) {
    pages.push(DOTS);
  }

  // Pages around current
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  // Show right DOTS if needed
  if (current < total - 2) {
    pages.push(DOTS);
  }

  // Always show last page
  if (total > 1) {
    pages.push(total);
  }

  return pages;
}
